import { Control, useFieldArray, useWatch } from 'react-hook-form';
import { useState } from 'react';
import { useTranslation } from '@/i18n';
import { Label } from '../ui/label';
import StyledFormSection from '../styled/StyledFormSection';
import { YesNoStyledRadioGroup } from '../styled/StyledRadioGroup';
import StyledButton from '../styled/StyledButton';
import HotelAssociatedBenefitAdditionModal, {
  type AssociatedBenefitDisplay,
} from './HotelAssociatedBenefitAdditionModal';
import AssociatedBenefitsTable from './AssociatedBenefitsTable';
import { HOTEL_ASSOCIATED_BENEFIT_LIST_COLUMNS } from '@/types/benefits/hotelAssociatedBenefitColumnDef';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import { HotelFormDataWithTranslations } from '@/i18n/translation-type-utils';
import type { AssociatedBenefitFrequency } from '@/types/benefits/associated-benefit-frequency';

interface HotelAssociatedBenefitsSectionProps {
  control: Control<HotelFormDataWithTranslations>;
  isViewMode?: boolean;
}

const HotelAssociatedBenefitsSection = ({
  control,
  isViewMode = false,
}: HotelAssociatedBenefitsSectionProps) => {
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();

  const shouldAssociateBenefits = useWatch({
    control,
    name: 'shouldAssociateBenefits',
  });

  const [isAddAssociatedBenefitModalOpen, setIsAddAssociatedBenefitModalOpen] =
    useState(false);

  const {
    fields: associatedBenefitsFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'associatedBenefits',
  });

  return (
    <StyledFormSection sectionTitle={t('hotels.associatedBenefits.title')}>
      <Label className="text-gray-700">
        {t('hotels.associatedBenefits.question')}
      </Label>
      <YesNoStyledRadioGroup
        control={control}
        name="shouldAssociateBenefits"
        disabled={isViewMode}
      />
      {shouldAssociateBenefits === 'true' && (
        <>
          <StyledButton
            title={t('hotels.associatedBenefits.addBenefit')}
            onClick={() => setIsAddAssociatedBenefitModalOpen(true)}
            disabled={isViewMode}
          />
          {associatedBenefitsFields.length === 0 ? (
            <div className="p-4 text-center text-gray-500 border rounded-md">
              {t('hotels.associatedBenefits.noBenefits')}
            </div>
          ) : (
            <AssociatedBenefitsTable
              fields={associatedBenefitsFields}
              remove={remove}
              isViewMode={isViewMode}
              columns={getTranslatedConst(
                HOTEL_ASSOCIATED_BENEFIT_LIST_COLUMNS,
              )}
            />
          )}
          <HotelAssociatedBenefitAdditionModal
            open={isAddAssociatedBenefitModalOpen}
            onClose={() => setIsAddAssociatedBenefitModalOpen(false)}
            existingBenefitIds={associatedBenefitsFields.map(
              (f) => f.benefitId,
            )}
            isViewMode={isViewMode}
            onAdd={(
              benefitId: string,
              frequency: AssociatedBenefitFrequency,
              benefitDisplay: AssociatedBenefitDisplay,
            ) => {
              append({
                benefitId,
                frequency,
                benefit: {
                  id: benefitDisplay.id,
                  name: benefitDisplay.name,
                  code: benefitDisplay.code,
                  type: benefitDisplay.type,
                  country: benefitDisplay.country,
                  supplier: benefitDisplay.supplier,
                  activity: benefitDisplay.activity,
                },
              });
              setIsAddAssociatedBenefitModalOpen(false);
            }}
          />
        </>
      )}
    </StyledFormSection>
  );
};

export default HotelAssociatedBenefitsSection;
